import { Link } from "react-router-dom";
import {
  Bank, Coins, TrendUp, UsersThree, ArrowRight,
  ShieldCheck, ChartLineUp, Receipt,
  House, ForkKnife, Tree, Hammer, Wrench, Briefcase, Car,
  Lightning, Truck, HardHat, Tooth, Tractor, HandCoins, Factory, Buildings,
} from "@phosphor-icons/react";
import { PRESETS } from "@/lib/presets";
import { fmtCurrency } from "@/lib/format";

const NICHE_ICONS = {
  House, ForkKnife, Tree, Hammer, Wrench, Briefcase, Car,
  Lightning, Truck, HardHat, Tooth, Tractor, HandCoins, Factory, Buildings,
};

const SHOWCASE_NICHES = PRESETS.filter((p) => p.id !== "homeowner");

const pillars = [
  {
    icon: Coins,
    title: "Passive Income Tax",
    body:
      "Ontario CCPCs lose 50.17% of corporate interest to tax. Inside an IBC policy, that growth is tax-deferred — the compounding difference alone can be transformational.",
    stat: "50.17%",
    statLabel: "Passive income tax drag",
  },
  {
    icon: Receipt,
    title: "Interest Recapture",
    body:
      "Every dollar paid to Ford Credit, CWB, or the bank for fleet financing is interest you'll never see again. Finance through your own policy and capture it back.",
    stat: "7.9%",
    statLabel: "Avg. fleet loan rate",
  },
  {
    icon: TrendUp,
    title: "Dividend Projection",
    body:
      "The 2026 participating dividend scale sits between 6.25% – 6.40%. Project 5- and 10-year cash value growth on every premium dollar.",
    stat: "6.35%",
    statLabel: "2026 mid-scale",
  },
  {
    icon: UsersThree,
    title: "Golden Handcuffs",
    body:
      "Key foremen walking next spring? Allocate policy cash value as a retention vehicle — keep the talent, and keep the asset on your balance sheet.",
    stat: "$60K",
    statLabel: "Avg. turnover cost",
  },
];

export default function Landing() {
  return (
    <main data-testid="landing-page">
      <Hero />
      <NicheShowcase />
      <Pillars />
      <WhoItsFor />
      <LandingFooter />
    </main>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-brand-line">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1708694557066-c9437fcfdc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ3JlZW4lMjBhYnN0cmFjdCUyMHRleHR1cmV8ZW58MHx8fHwxNzc3MDYyMjU5fDA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: "cover",
          backgroundPosition: "right center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-bone via-brand-bone/95 to-brand-bone/30" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <span className="label-eyebrow">For Ontario business owners · CCPC</span>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-brand-forest tracking-tight">
            Build a bank.
            <br />
            <span className="italic text-brand-copper">Don't borrow</span> from one.
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink/80 leading-relaxed">
            Your business is likely leaking tens of thousands every year — through
            passive-income tax, fleet financing interest, and key people walking out
            the door. Pick your industry, plug in three numbers, and see the 10-year
            wealth gap you never knew existed.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/quick-breakdown"
              data-testid="hero-cta-quickbreakdown"
              className="inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-6 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5"
            >
              See how much you're leaking <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="/advisor-login"
              data-testid="hero-cta-advisor"
              className="inline-flex items-center gap-2 rounded-md border border-brand-forest/30 hover:border-brand-forest bg-white text-brand-forest px-6 py-3.5 text-sm font-semibold transition-colors"
            >
              Advisor sign in
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-8 max-w-xl">
            <Stat k="$2M+" v="10-yr wealth gain potential" />
            <Stat k="50.17%" v="Passive tax avoided" />
            <Stat k="6.35%" v="Projected dividend scale" />
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up">
          <div className="relative rounded-md overflow-hidden border border-brand-line shadow-xl">
            <img
              src="https://images.unsplash.com/photo-1758519288372-045de3f65147?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxhZHZpc29yJTIwY2xpZW50JTIwbWVldGluZ3xlbnwwfHx8fDE3NzcwNjIyMzl8MA&ixlib=rb-4.1.0&q=85"
              alt="Advisor in client meeting"
              className="w-full h-[440px] object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-forest/95 via-brand-forest/70 to-transparent p-6">
              <div className="text-brand-bone/70 text-xs uppercase tracking-[0.2em] font-semibold">
                Case · Bay Roofing · North Bay, ON
              </div>
              <div className="mt-2 text-brand-bone font-serif text-2xl leading-tight">
                15 trucks, 4 foremen, $200K/yr retained — projected gain of $2.3M
                by year 10.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function NicheShowcase() {
  return (
    <section className="border-b border-brand-line bg-white" data-testid="niche-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-3xl">
          <span className="label-eyebrow">Potential savings by industry</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            What businesses like yours
            <br />
            <span className="text-brand-copper">typically leak — and recover.</span>
          </h2>
          <p className="mt-5 text-brand-ink/75 text-lg leading-relaxed">
            Based on industry-average fleets, retained earnings, and key staff for
            Ontario CCPCs. Find your industry, then run your own numbers — it takes
            thirty seconds.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SHOWCASE_NICHES.map((p) => (
            <NicheCard key={p.id} preset={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function NicheCard({ preset }) {
  const Icon = NICHE_ICONS[preset.icon] || Briefcase;
  const { showcase } = preset;
  return (
    <Link
      to={`/quick-breakdown?industry=${preset.id}`}
      data-testid={`niche-card-${preset.id}`}
      className="group bg-white border border-brand-line rounded-md p-6 flex flex-col transition-all hover:-translate-y-1 hover:shadow-lg hover:border-brand-forest/40"
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-brand-cream text-brand-forest">
          <Icon size={20} weight="duotone" />
        </span>
        <div className="font-serif text-xl text-brand-forest leading-tight">{preset.label}</div>
      </div>
      <p className="mt-3 text-xs text-brand-ink/60 leading-relaxed">{preset.blurb}</p>

      <div className="mt-5 pt-4 border-t border-brand-line space-y-2.5">
        <div className="flex justify-between items-baseline text-sm">
          <span className="text-brand-ink/60">Typically leaking</span>
          <span className="font-semibold text-red-800 tabular-nums">
            ~{fmtCurrency(showcase.leak)}/yr
          </span>
        </div>
        <div className="flex justify-between items-baseline text-sm">
          <span className="text-brand-ink/60">5-yr potential gain</span>
          <span className="font-semibold text-brand-forest tabular-nums">{fmtCurrency(showcase.gain5)}</span>
        </div>
        <div className="flex justify-between items-baseline text-sm">
          <span className="text-brand-ink/60">10-yr potential gain</span>
          <span className="font-serif text-xl text-brand-copper tabular-nums">{fmtCurrency(showcase.gain10)}</span>
        </div>
      </div>

      <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-copper group-hover:gap-2.5 transition-all">
        Run my numbers <ArrowRight size={13} weight="bold" />
      </div>
    </Link>
  );
}

function Pillars() {
  return (
    <section className="border-b border-brand-line bg-brand-cream/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-3xl">
          <span className="label-eyebrow">The four levers</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            Where the money is actually going.
          </h2>
          <p className="mt-5 text-brand-ink/75 text-lg leading-relaxed">
            Most business owners know they're "losing money somewhere." This tool names
            exactly where — and models the 10-year recovery if they put the policy in
            the middle of it.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map(({ icon: Icon, title, body, stat, statLabel }, i) => (
            <div
              key={title}
              data-testid={`pillar-card-${i}`}
              className="group relative bg-white border border-brand-line rounded-md p-7 transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-brand-cream text-brand-forest">
                <Icon weight="duotone" size={24} />
              </div>
              <div className="mt-5 font-serif text-2xl text-brand-forest">{title}</div>
              <p className="mt-3 text-sm text-brand-ink/70 leading-relaxed">{body}</p>
              <div className="mt-6 pt-5 border-t border-brand-line flex items-baseline justify-between">
                <span className="font-serif text-3xl text-brand-copper">{stat}</span>
                <span className="text-[11px] uppercase tracking-[0.16em] text-brand-muted">
                  {statLabel}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhoItsFor() {
  return (
    <section className="bg-brand-cream/60 border-b border-brand-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="rounded-md overflow-hidden border border-brand-line shadow-md">
            <img
              src="https://images.pexels.com/photos/33404248/pexels-photo-33404248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Roofing contractor at work"
              className="w-full h-[440px] object-cover"
            />
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <span className="label-eyebrow">Who this is for</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            The Ontario business owner.
          </h2>
          <p className="mt-5 text-brand-ink/75 text-lg leading-relaxed">
            Trades, trucking, restaurants, clinics, leasing companies, multi-location
            corporations — any CCPC with
            <span className="text-brand-forest font-semibold"> real retained earnings,
            financed vehicles or equipment, and a handful of people they can't afford to lose.</span>
          </p>
          <ul className="mt-8 space-y-4">
            {[
              { icon: ShieldCheck, text: "Quiet, tax-deferred growth inside a CCPC-owned policy." },
              { icon: ChartLineUp, text: "Financing interest recaptured into your own balance sheet." },
              { icon: UsersThree, text: "Golden-handcuff cash values that keep your key people home." },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex gap-3 items-start">
                <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-forest/10 text-brand-forest">
                  <Icon size={16} weight="bold" />
                </span>
                <span className="text-brand-ink/80">{text}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/quick-breakdown"
            data-testid="who-its-for-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-md bg-brand-copper hover:bg-brand-copperHover text-white px-6 py-3 text-sm font-semibold transition-colors"
          >
            See your leakage <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="bg-brand-forest text-brand-bone/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Bank weight="duotone" size={18} className="text-brand-copper" />
            <span className="font-serif text-lg text-brand-bone">Build A Bank</span>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-bone/50">
            A quick-breakdown tool for Ontario businesses · 2026
          </div>
        </div>
        <div className="pt-6 border-t border-brand-bone/15 text-[11px] leading-relaxed text-brand-bone/55 max-w-4xl">
          <span className="block label-eyebrow text-brand-copper/80 mb-2">Disclaimer</span>
          <p>
            This calculator provides a hypothetical illustration based on user-entered assumptions and is not
            legal, tax, accounting, or insurance advice. Dividends, tax rates, and returns are not guaranteed and
            are subject to change. Any participating-life-insurance product referenced must be evaluated against
            the issuing insurer's policy contract and a licensed-advisor suitability review. Clients should
            obtain independent professional advice before making financial decisions.
          </p>
        </div>
      </div>
    </footer>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="font-serif text-3xl text-brand-forest">{k}</div>
      <div className="mt-1 text-xs text-brand-muted uppercase tracking-[0.14em]">{v}</div>
    </div>
  );
}
